import { apiCall, apiUpload } from '@/lib/api-client';
import type {
  AttestationStatus, FindhelpHistory, NavStatus, PreauthStatus,
} from './navigator.api';

export interface AdminNavSummary {
  byStatus: Record<string, number>;
  byFindhelpHistory: Record<string, number>;
  urgent: number;
  unclaimed: number;
  slaOverdue: number;
  slaDueSoon: number;
  awaitingFirstContact: number;
  /** Named parts so W8/W12 can add theirs without breaking this screen. */
  pendingMirror: { total: number; parts: Record<string, number> };
  /** Surface -> the wave that builds it. Rendered as "coming in W11", not as 0. */
  notBuiltYet: Record<string, string>;
  // F5-R4a — el server YA emite estos tres agregados (adminSummary, Wave 11 y
  // GC-3) pero el cliente no los declaraba: quedaban invisibles para la UI.
  // Declararlos no cambia el contrato — solo deja de tirarlos al piso.
  /** Tracks por estado de attestation (Wave 11). */
  byAttestation?: Record<string, number>;
  /** Tracks por estado de preautorización — pendiente + en_revision = esperando firma. */
  byPreauth?: Record<string, number>;
  /** Cierres por razón (closed_reason_code) — total histórico, sin corte por período. */
  byClosedReason?: Record<string, number>;
}

export interface AdminNavByNavigator {
  window: 'today' | 'week' | 'month';
  checklistTotal: number;
  rows: Array<{
    navigatorId: string;
    navigatorName: string;
    casesWorked: number;
    openCases: number;
    attempts: number;
    sessions: number;
    units: number;
    reportedMinutes: number;
    timerMinutes: number;
    deltaMinutes: number | null;
    timedSessions: number;
    checklistAvg: number;
    pendingMirror: number;
  }>;
}

export interface AdminNavCaseRow {
  id: string;
  clientId: string;
  status: NavStatus;
  priority: 'normal' | 'urgent';
  somosPriority: boolean;
  findhelpHistory: FindhelpHistory;
  autoComputed: boolean;
  manuallyOverridden: boolean;
  nextStepNavigator: boolean;
  positiveDomains: string[];
  slaDeadline: string | null;
  outreachAttempts: number;
  claimNavigatorId: string | null;
  claimNavigatorName: string | null;
  clientLastName: string;
  cinHashSuffix: string;
  notesPendingSync: number;
  createdAt: string;
}

export interface AdminNavListResponse {
  items: AdminNavCaseRow[];
  total: number;
  limit: number;
  offset: number;
  counts: Record<string, number>;
  suggestedPendingReview: number;
}

export interface AdminNavFilters {
  status?: string;
  search?: string;
  urgent?: boolean;
  somos?: boolean;
  overdue?: boolean;
  findhelpHistory?: string;
  /** GC-3 #3: cómo cerró el episodio (catálogo de razones; incluye no_consent_ea). */
  closedReason?: string;
  limit?: number;
  offset?: number;
}

function qs(f: AdminNavFilters): string {
  const p = new URLSearchParams();
  if (f.status && f.status !== 'all') p.set('status', f.status);
  if (f.search) p.set('search', f.search);
  if (f.urgent) p.set('urgent', 'true');
  if (f.somos) p.set('somos', 'true');
  if (f.overdue) p.set('overdue', 'true');
  if (f.findhelpHistory && f.findhelpHistory !== 'all') p.set('findhelpHistory', f.findhelpHistory);
  if (f.closedReason && f.closedReason !== 'all') p.set('closedReason', f.closedReason);
  if (f.limit) p.set('limit', String(f.limit));
  if (f.offset) p.set('offset', String(f.offset));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * GC-3 #3 — «Descargar CSV» del listado filtrado. Mismo patrón fetch+blob de
 * los otros exports (la auth es Bearer: una URL pelada no viaja con el token,
 * la lección de NAV-B-14 sobre VITE_API_BASE aplica igual). El server audita
 * ADMIN_PHI_EXPORT_AD_HOC antes de servir la primera fila.
 */
export async function downloadAdminNavCsv(
  f: AdminNavFilters,
  accessToken: string,
): Promise<void> {
  const { limit: _l, offset: _o, ...rest } = f;
  const base = `${import.meta.env.VITE_API_BASE ?? ''}/api`;
  const res = await fetch(`${base}/admin/navigation/export.csv${qs(rest)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  const disp = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^";]+)"?/.exec(disp);
  a.download = m?.[1] ?? `navigation-cases-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

export const adminNavApi = {
  summary: () => apiCall<AdminNavSummary>('/admin/navigation/summary'),
  byNavigator: (window: 'today' | 'week' | 'month') =>
    apiCall<AdminNavByNavigator>(`/admin/navigation/by-navigator?window=${window}`),
  list: (f: AdminNavFilters) => apiCall<AdminNavListResponse>(`/admin/navigation${qs(f)}`),
  override: (
    id: string,
    body: { status?: string; priority?: string; releaseClaim?: boolean; reason?: string },
  ) => apiCall<{ id: string; changes: Record<string, unknown> }>(`/admin/navigation/${id}`, {
    method: 'PATCH', body,
  }),
  waiveStep: (id: string, step: number, reason: string) =>
    apiCall<unknown>(`/admin/navigation/${id}/checklist/${step}/waive`, {
      method: 'PATCH', body: { reason },
    }),

  exceptions: () => apiCall<AdminNavExceptions>('/admin/navigation/exceptions'),
  assignmentLoads: () => apiCall<AdminNavLoad[]>('/admin/navigation/assignment-loads'),
  settings: () => apiCall<Record<string, unknown>>('/admin/navigation/settings'),
  updateSetting: (key: string, value: unknown) =>
    apiCall<{ key: string; value: unknown }>(`/admin/navigation/settings/${encodeURIComponent(key)}`, {
      method: 'PATCH', body: { value },
    }),
  reassignMember: (id: string, body: { navigatorId: string; reason: string }) =>
    apiCall<AdminNavReassignResult>(`/admin/navigation/${id}/reassign-member`, {
      method: 'POST', body,
    }),
  caseForms: (id: string) => apiCall<AdminNavFormRow[]>(`/admin/navigation/${id}/forms`),

  // ---------------------------------------------------------------- NAV-B-11
  // Las rutas admin que el controller ya exponía sin función cliente. Sin esto
  // el admin no podía desbloquear un caso parkeado ni VER lo que va a firmar.

  /** Levanta el parking de outreach (caso con intentos agotados). */
  unblockOutreach: (id: string) =>
    apiCall<{ unblocked: true }>(`/admin/navigation/${id}/outreach/unblock`, { method: 'POST' }),
  caseTracks: (id: string) => apiCall<AdminNavTrackRow[]>(`/admin/navigation/${id}/tracks`),
  caseAttachments: (id: string) =>
    apiCall<AdminNavAttachmentRow[]>(`/admin/navigation/${id}/attachments`),
  /**
   * NAV-A-01 — la firma de la preautorización (manual §VIII). `denialReason` es
   * obligatorio del lado del servidor cuando status='denegada'
   * (DENIAL_REASON_REQUIRED): sin el motivo que mandó SOMOS no hay nada que
   * corregir, y corregir-y-re-someter es todo el procedimiento que existe.
   */
  setPreauthStatus: (trackId: string, status: PreauthStatus, denialReason?: string) =>
    apiCall<{ trackId: string; status: string }>(
      `/admin/navigation/tracks/${trackId}/preauth-status`,
      { method: 'PATCH', body: { status, denialReason } },
    ),

  /** Priority-list import — dry-run match by CIN (xlsx/csv/txt or cins[]). */
  priorityListPreview: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiUpload<PriorityListPreview>('/admin/navigation/priority-list/preview', fd);
  },
  priorityListPreviewCins: (cins: string[]) =>
    apiCall<PriorityListPreview>('/admin/navigation/priority-list/preview', {
      method: 'POST', body: { cins },
    }),
  /** Admin-only apply: set somos_priority + optional create/assign. */
  priorityListApply: (
    file: File,
    opts: { createMissingCases?: boolean; navigatorId?: string },
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts.createMissingCases) fd.append('createMissingCases', 'true');
    if (opts.navigatorId) fd.append('navigatorId', opts.navigatorId);
    return apiUpload<PriorityListApplyResult>('/admin/navigation/priority-list/apply', fd);
  },
  priorityListApplyCins: (
    cins: string[],
    opts: { createMissingCases?: boolean; navigatorId?: string },
  ) =>
    apiCall<PriorityListApplyResult>('/admin/navigation/priority-list/apply', {
      method: 'POST',
      body: { cins, ...opts },
    }),

  bulkReassignPreview: (body: {
    filter: BulkReassignFilter;
    navigatorId: string;
    limit: number;
    reason?: string;
    /** F5-P2 — repartir DESDE este navigator. */
    fromNavigatorId?: string;
  }) =>
    apiCall<BulkReassignPreview>('/admin/navigation/bulk-reassign/preview', {
      method: 'POST',
      body,
    }),
  bulkReassignApply: (body: {
    filter: BulkReassignFilter;
    navigatorId: string;
    limit: number;
    reason: string;
    /** F5-P2 — repartir DESDE este navigator. */
    fromNavigatorId?: string;
  }) =>
    apiCall<BulkReassignApplyResult>('/admin/navigation/bulk-reassign/apply', {
      method: 'POST',
      body,
    }),
  /** Dedicated navigator list for the reassignment dropdown (not assignment-loads). */
  bulkReassignNavigators: () =>
    apiCall<BulkReassignNavigator[]>('/admin/navigation/bulk-reassign/navigators'),
  bulkReassignPoolCount: (filter: BulkReassignFilter) =>
    apiCall<BulkReassignPoolCount>(
      `/admin/navigation/bulk-reassign/pool-count?filter=${encodeURIComponent(filter)}`,
    ),
  /**
   * F5-P3 — los cuatro conteos de la piscina de una sola vez. Adrede NO es
   * cuatro pool-count: el server los saca en una pasada, y así los cuatro
   * números siempre son de la misma foto (si no, la suma no cerraría).
   */
  bulkReassignPoolViews: () =>
    apiCall<PoolViewCounts>('/admin/navigation/bulk-reassign/pool-views'),
  /** F5-P2 — orígenes: incluye a los dados de baja que todavía tienen carga. */
  bulkReassignSources: () =>
    apiCall<BulkReassignSource[]>('/admin/navigation/bulk-reassign/sources'),
  bulkReassignSourceCount: (fromNavigatorId: string, filter: BulkReassignFilter) =>
    apiCall<BulkReassignSourceCount>(
      `/admin/navigation/bulk-reassign/source-count?fromNavigatorId=${encodeURIComponent(fromNavigatorId)}`
      + `&filter=${encodeURIComponent(filter)}`,
    ),

  // ---------------------------------------------------------------- E-36
  // «Repartir parejo»: el emparejamiento a mano que el server ya tiene desde
  // E-35b (`admin-navigation.controller.ts`, POST rebalance + GET
  // rebalance/runs). El POST es SOLO admin; responde 202 enseguida y el
  // trabajo corre en segundo plano, así que el resultado se mira en `runs`.
  /** Arranca una corrida a mano del tipo dado. No espera a que termine. */
  rebalance: (scope: RebalanceScope) =>
    apiCall<RebalanceStartResult>('/admin/navigation/rebalance', {
      method: 'POST',
      body: { scope },
    }),
  /** Las últimas 20 corridas (más nueva primero). Sin PHI: ids y conteos. */
  rebalanceRuns: () =>
    apiCall<{ items: RebalanceRunRow[] }>('/admin/navigation/rebalance/runs'),
  // Adrede SIN función cliente: PATCH tracks/:id/attestation-status y
  // tracks/:id/service existen en el controller pero su flujo es del navigator
  // (NeedCards) — una función acá sin botón sería el mismo muerto que NAV-B-11
  // vino a limpiar.
};

export interface PriorityListPreview {
  filename: string | null;
  totalRows: number;
  uniqueCins: number;
  matched: number;
  notFound: number;
  notFoundSample: string[];
  alreadyPriority: number;
  openCase: number;
  eligibleForCreate: number;
  matchedNoEligible: number;
}

export interface PriorityListApplyResult extends PriorityListPreview {
  prioritySet: number;
  casesCreated: number;
  reassigned: number;
  dryRun: false;
}

export type BulkReassignFilter =
  | 'findhelp_positives'
  | 'findhelp_negatives'
  // POOL-B2 (D-1): reemplaza al muerto 'unassigned' (el CHECK del dueño hace
  // imposible un abierto sin dueño) — «En el buzón» = dueño con scope 'pool'.
  | 'in_pool'
  | 'somos'
  | 'open'
  | PoolView;

/**
 * F5-P3 · Las cuatro vistas de la piscina (firma CEO 2026-08-30). La piscina
 * es UNA sola — el buzón con `navigator_scope='pool'` — y estas cuatro la
 * PARTEN: cada caso del buzón cae en exactamente una, y los cuatro conteos
 * suman `in_pool`. La transición entre vistas es automática: la escribe el
 * uploader al procesar el screening; no hay casos que mover.
 */
export type PoolView =
  /** El screening entró pero el uploader NO lo procesó. NO se reparte. */
  | 'pool_awaiting_verification'
  /** Positivos con Medicaid confirmado — de acá reparte el CEO. */
  | 'pool_ready_to_deal'
  /** Verificados que no califican. */
  | 'pool_no_medicaid'
  /** Sin ninguna necesidad positiva — el cierre masivo. */
  | 'pool_negatives';

export const POOL_VIEWS: PoolView[] = [
  'pool_awaiting_verification',
  'pool_ready_to_deal',
  'pool_no_medicaid',
  'pool_negatives',
];

/** Los cuatro conteos, en UNA llamada (el server los saca en una query). */
export interface PoolViewCounts {
  awaitingVerification: number;
  readyToDeal: number;
  noMedicaid: number;
  negatives: number;
  /** El total del buzón — la suma exacta de los cuatro. */
  inPool: number;
}

/** F5-P2 — espejo de `ReassignSkipReason` de agentes, para navegación. */
export interface NavReassignSkipCounts {
  OPEN_SESSION: number;
  NAV_APPOINTMENT_SCHEDULED: number;
  FOLLOWUP_ACCEPTED_PENDING: number;
  /** El miembro tiene otro caso abierto fuera de esta vista: no puede ir entero. */
  MEMBER_SPLIT_BY_FILTER: number;
  /**
   * E-36 · el server lo devuelve desde E-35d (`NavReassignSkipCounts` de
   * nav-bulk-reassign.service.ts): origen ACTIVO y el paciente ya tiene
   * trabajo (contacto, cita, sesión…), así que se queda con esa persona. Solo
   * tipo: declararlo no cambia el contrato, deja de tirarlo al piso.
   */
  MEMBER_HAS_WORK: number;
}

/**
 * E-36 · espejo de `ModoOrigen` del server: cómo se reparte desde el origen.
 * `buzon` = el pool · `activo` = solo los nunca contactados · `baja` = todo,
 * con citas y sesiones.
 */
export type NavReassignOriginMode = 'buzon' | 'activo' | 'baja';

export interface BulkReassignPreview {
  filter: BulkReassignFilter;
  candidateCount: number;
  willAssign: number;
  limit: number;
  navigatorId: string;
  navigatorName: string;
  skippedOpenSession: number;
  /** Cuantos del filtro YA son del destino elegido (explica el preview en cero). */
  alreadyTarget: number;
  negativesWithoutCase: number;
  sampleCaseIds: string[];
  // F5-P2 — null/0 cuando se reparte por filtro (sin origen).
  fromNavigatorId: string | null;
  fromNavigatorName: string | null;
  fromNavigatorActive: boolean | null;
  /** E-36 (solo tipo; el server lo emite desde E-35d). Null = «Del buzón». */
  fromMode?: NavReassignOriginMode | null;
  sourceOpenCases: number;
  movableCases: number;
  movableClients: number;
  skippedByReason: NavReassignSkipCounts;
}

/** F5-P2 — un origen posible: incluye INACTIVOS con carga, y el buzón. */
export interface BulkReassignSource extends BulkReassignNavigator {
  active: boolean;
}

export interface BulkReassignSourceCount {
  fromNavigatorId: string;
  fromNavigatorName: string;
  fromNavigatorActive: boolean;
  /** E-36 (solo tipo; el server lo emite desde E-35d). */
  fromMode?: NavReassignOriginMode;
  filter: BulkReassignFilter;
  openCases: number;
  movableCases: number;
  movableClients: number;
  skippedByReason: NavReassignSkipCounts;
  skippedCases: number;
}

export interface BulkReassignApplyResult extends BulkReassignPreview {
  applied: number;
  movedCaseIds: string[];
  dryRun: false;
}

export interface BulkReassignNavigator {
  navigatorId: string;
  navigatorName: string;
  openEpisodes: number;
  /** 'full' | 'closer' — el buzón ('pool') jamás llega a esta lista. */
  navigatorScope: string;
}

export interface BulkReassignPoolCount {
  filter: BulkReassignFilter;
  poolCount: number;
}

/** E-36 · los dos tipos que se emparejan: navegadoras (`full`) y closers. */
export type RebalanceScope = 'full' | 'closer';

/**
 * E-36 · respuesta del POST rebalance (202). `started:false` trae el porqué:
 * `switch_off` (closers con el interruptor apagado en Config) o
 * `already_running` (ya hay una corrida viva de ese tipo).
 */
export interface RebalanceStartResult {
  runId: string | null;
  started: boolean;
  reason?: string;
}

/** E-36 · una fila de `rebalance/runs` — espejo de `RebalanceRunRow` del server. */
export interface RebalanceRunRow {
  id: string;
  scope: RebalanceScope;
  /** 'manual' | 'user_created' | 'user_activated' */
  trigger: string;
  triggeredBy: string | null;
  status: 'running' | 'done' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  groupSize: number | null;
  target: number | null;
  supplyPool: number | null;
  supplyInactive: number | null;
  movedCases: number;
  movedClients: number;
  skipped: Record<string, number>;
  /** 'switch_off' | 'empty_group' | 'stale' | null */
  reason: string | null;
  error: string | null;
}

/**
 * Bajar un adjunto desde la superficie admin. No pasa por `apiCall` porque la
 * respuesta es un archivo, no JSON. La URL se arma con el mismo VITE_API_BASE
 * del api-client (NO con '/api' pelado — la lección de NAV-B-14). Cada descarga
 * queda auditada del lado del servidor.
 */
export async function downloadAdminAttachment(
  attachmentId: string,
  filename: string,
  accessToken: string,
): Promise<void> {
  const base = `${import.meta.env.VITE_API_BASE ?? ''}/api`;
  const res = await fetch(`${base}/admin/navigation/attachments/${attachmentId}/download`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

export interface AdminNavExceptionItem {
  caseId: string;
  clientId: string;
  navigatorId: string | null;
  navigatorName: string | null;
  pendingForms: number;
  unreimbursedSessions: number;
  overdueFollowups: number;
  timerDiscrepancies: number;
  staleAppointments: number;
  unitsExhausted: boolean;
  overridden: boolean;
  /**
   * Parking de outreach (outreach_exhausted_at). Opcional: el supervisorDashboard
   * del backend todavía no lo emite — cuando lo haga, el botón "desbloquear" se
   * resalta solo en los casos parkeados en vez de ofrecerse a ciegas.
   */
  outreachExhausted?: boolean;
}

/** Lo que la vista de firma necesita de cada track (GET :id/tracks). */
export interface AdminNavTrackRow {
  id: string;
  serviceKey: string;
  preauthRequired: boolean;
  attestationStatus: AttestationStatus;
  attestationSentAt: string | null;
  preauthStatus: PreauthStatus;
  preauthSentAt: string | null;
  createdAt: string;
  findhelpReferralId: string | null;
  referredAt: string | null;
  referJustification: string | null;
  notReferredReason: string | null;
  preauthDenialReason: string | null;
  preauthResubmittedAt: string | null;
  preauthResubmitCount: number;
  cancelledAt: string | null;
  cancelReason: string | null;
  serviceStatus: string;
  serviceResult: string | null;
  serviceCompletedAt: string | null;
}

/**
 * Fila de GET :id/attachments. El backend devuelve el SQL crudo (snake_case) —
 * se tipa lo que la pantalla usa; el resto viaja igual.
 */
export interface AdminNavAttachmentRow {
  id: string;
  kind: string;
  filename: string;
  mime: string;
  size: string;
  created_at: string;
  service_key: string | null;
  form_type: string | null;
  uploaded_by: string | null;
  expired: boolean;
}

export interface AdminNavExceptions {
  counts: {
    pendingForms: number;
    unreimbursedSessions: number;
    overdueFollowups: number;
    timerDiscrepancies: number;
    staleAppointments: number;
    unitsExhausted: number;
    overrides: number;
  };
  items: AdminNavExceptionItem[];
}

export interface AdminNavLoad {
  navigatorId: string;
  navigatorName: string;
  openEpisodes: number;
  overdueFollowups: number;
  upcomingAppointments: number;
  score: number;
}

export interface AdminNavReassignResult {
  clientId: string;
  fromNavigatorIds: string[];
  toNavigatorId: string;
  cases: number;
  futureAppointments: number;
  closedSessions: number;
}

export interface AdminNavFormRow {
  id: string;
  formType: string;
  status: string;
  findhelpId: string | null;
  confirmation: string | null;
  submittedAt: string | null;
  actorName: string | null;
  exceptionReason: string | null;
  attachmentCount: number;
}

export const NAV_OPERATIONAL_SETTING_KEYS = [
  'nav_assignment_weight_open_episode',
  'nav_assignment_weight_overdue_followup',
  'nav_assignment_weight_upcoming_appointment',
  'nav_assignment_appointment_horizon_days',
  'nav_ea_validity_days',
  'nav_timer_idle_minutes',
  'nav_timer_idle_espejo_minutes',
  'nav_timer_max_minutes',
  'nav_timer_delta_threshold_minutes',
  'nav_no_show_grace_minutes',
  'nav_general_followup_days',
  'nav_referral_no_response_business_days',
  'nav_findhelp_base_url',
] as const;

export const ADMIN_NAV_KEYS = {
  summary: ['admin', 'navigation', 'summary'] as const,
  byNavigator: (w: string) => ['admin', 'navigation', 'by-navigator', w] as const,
  list: (f: AdminNavFilters) => ['admin', 'navigation', 'list', f] as const,
  exceptions: ['admin', 'navigation', 'exceptions'] as const,
  loads: ['admin', 'navigation', 'assignment-loads'] as const,
  bulkNavigators: ['admin', 'navigation', 'bulk-reassign', 'navigators'] as const,
  bulkPool: (f: string) => ['admin', 'navigation', 'bulk-reassign', 'pool', f] as const,
  /** F5-P3 — los cuatro conteos de la piscina (una query, una foto). */
  bulkPoolViews: ['admin', 'navigation', 'bulk-reassign', 'pool-views'] as const,
  // F5-P2 — el camino por origen.
  bulkSources: ['admin', 'navigation', 'bulk-reassign', 'sources'] as const,
  bulkSourceCount: (id: string, f: string) =>
    ['admin', 'navigation', 'bulk-reassign', 'source-count', id, f] as const,
  /** E-36 — las corridas del emparejamiento («Repartir parejo»). */
  rebalanceRuns: ['admin', 'navigation', 'rebalance', 'runs'] as const,
  settings: ['admin', 'navigation', 'settings'] as const,
  forms: (id: string) => ['admin', 'navigation', 'forms', id] as const,
  tracks: (id: string) => ['admin', 'navigation', 'tracks', id] as const,
  attachments: (id: string) => ['admin', 'navigation', 'attachments', id] as const,
};
